import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import merge from 'lodash/merge';
import {
  Drawer,
  DrawerContent,
  useDrawerState,
  ApplicationUIContext,
  SummaryGrid,
} from '@yuhuinc/ui-library';
import { Form, Typography, Button, Space, Input } from 'antd';

import { buildInitialValuesForSelectedApplicant } from './helpers';

import AssignApplicant from '../assign_applicant';
import NewOrEditApplicant from './NewOrEditApplicant';

import { getLead, getApplicantDeposits } from '../store/actions';
import { getTranslatedString } from 'helpers/i18n';
import { ContactMethod } from '../additional_information/ContactMethod';
import NewApplicantErrorAlert from './NewApplicantError';

const { Title } = Typography;

const SelectLeadApplicant = ({
  selectedApplicant,
  siteAndProperties,
  loadingSiteAndProperties,
  mode,
}) => {
  const { isMobile } = useContext(ApplicationUIContext);
  const form = Form.useFormInstance();
  const [, assignApplicantDrawer] = useDrawerState(false);
  const [, editApplicantInfoDrawer] = useDrawerState(false);
  const [newApplicantError, setNewApplicantError] = useState(null);

  const onAddApplicant = async (applicant) => {
    let selectedApplicant = applicant;

    // In 'create' mode, an applicant is a lead in the backend
    // In 'edit' mode, an applicant is a cotenant and the applicant object has all the information we need, so skip this step
    if (mode === 'create' && applicant.id && !applicant.erp_ids) {
      const applicantDetails = await getLead(applicant.id);
      selectedApplicant = merge(selectedApplicant, applicantDetails.lead);
    }

    if (applicant?.erp_ids) {
      const depositDetails = await getApplicantDeposits(applicant.erp_ids.user_id);
      selectedApplicant = merge(selectedApplicant, depositDetails);
    }

    form.setFieldsValue({ selectedApplicant });
  };

  const prepareApplicantForSummary = (applicant) => {
    if (!applicant) return [];

    return [
      [
        getTranslatedString('landlordPortal.applicationManagement.inviteLeadToApply.applicantName'),
        applicant.full_name
          ? applicant.full_name
          : `${applicant.first_name} ${applicant.last_name}`,
      ],
      [getTranslatedString('landlordPortal.users.tableColumns.email'), applicant.email],
      [getTranslatedString('landlordPortal.users.tableColumns.phone'), applicant.phone_number],
    ];
  };

  return (
    <>
      <Form.Item name="selectedApplicant" hidden>
        <Input />
      </Form.Item>

      <Title level={isMobile ? 4 : 5}>
        {getTranslatedString(
          'landlordPortal.applicationManagement.inviteLeadToApply.inviteApplicant'
        )}
      </Title>

      {selectedApplicant ? (
        <>
          <SummaryGrid values={prepareApplicantForSummary(selectedApplicant)} />
          <Space size="middle" direction={isMobile ? 'vertical' : 'horizontal'}>
            <Button size={isMobile && 'large'} onClick={assignApplicantDrawer.open}>
              {getTranslatedString(
                'landlordPortal.applicationManagement.inviteLeadToApply.changeApplicant'
              )}
            </Button>
            <Button size={isMobile && 'large'} onClick={editApplicantInfoDrawer.open}>
              {getTranslatedString(
                'landlordPortal.applicationManagement.inviteLeadToApply.editApplicantInfo'
              )}
            </Button>
          </Space>
        </>
      ) : (
        <>
          <Button type="primary" size={isMobile && 'large'} onClick={assignApplicantDrawer.open}>
            {getTranslatedString(
              'landlordPortal.applicationManagement.inviteLeadToApply.addApplicant'
            )}
          </Button>
        </>
      )}

      <Drawer
        title={getTranslatedString(
          'landlordPortal.applicationManagement.inviteLeadToApply.assignApplicant'
        )}
        visible={assignApplicantDrawer.isVisible}
        closable
        destroyOnClose
        onClose={assignApplicantDrawer.close}
      >
        <AssignApplicant
          siteAndProperties={siteAndProperties}
          selectedApplicant={selectedApplicant}
          loadingSiteAndProperties={loadingSiteAndProperties}
          closeDrawer={assignApplicantDrawer.close}
          onAdd={onAddApplicant}
        />
      </Drawer>
      {selectedApplicant && (
        <Drawer
          title={getTranslatedString(
            'landlordPortal.applicationManagement.inviteLeadToApply.editApplicantInfo'
          )}
          visible={editApplicantInfoDrawer.isVisible}
          closable
          destroyOnClose
          onClose={() => {
            setNewApplicantError(null);
            editApplicantInfoDrawer.close();
          }}
        >
          <DrawerContent>
            {newApplicantError && (
              <NewApplicantErrorAlert {...newApplicantError} isMobile={isMobile} />
            )}
            <NewOrEditApplicant
              initialValues={buildInitialValuesForSelectedApplicant(selectedApplicant)}
              extraFormItems={<ContactMethod />}
              onSubmit={(updatedApplicant) => {
                onAddApplicant(updatedApplicant);
                editApplicantInfoDrawer.close();
              }}
              onError={setNewApplicantError}
              onCancel={editApplicantInfoDrawer.close}
            />
          </DrawerContent>
        </Drawer>
      )}
    </>
  );
};

SelectLeadApplicant.propTypes = {
  selectedApplicant: PropTypes.object,
  siteAndProperties: PropTypes.array,
  onAddApplicant: PropTypes.func,
  loadingSiteAndProperties: PropTypes.bool,
  mode: PropTypes.oneOf(['create', 'edit']),
};

SelectLeadApplicant.defaultProps = {
  selectedApplicant: null,
  siteAndProperties: [],
  onAddApplicant: () => {},
  loadingSiteAndProperties: false,
};

export default SelectLeadApplicant;
